import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  getCurrentPlan,
  getAllPortalsWithStatus,
} from "../../utils/portalAccess";

const UpgradeModal = ({ isOpen, onClose, restrictedPortal = null }) => {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState("Basic");
  const [portalsStatus, setPortalsStatus] = useState([]);

  useEffect(() => {
    setCurrentPlan(getCurrentPlan());
    setPortalsStatus(getAllPortalsWithStatus());
  }, [isOpen]);

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2,
      },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[70vh] overflow-y-auto"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-t-2xl">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="text-center">
                <motion.div
                  className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </motion.div>

                <h2 className="text-3xl font-bold mb-2">
                  {restrictedPortal
                    ? `Unlock ${restrictedPortal} Portal`
                    : "Upgrade Your Plan"}
                </h2>
                <p className="text-blue-100 text-lg">
                  {restrictedPortal
                    ? `Access to ${restrictedPortal} portal requires a upgraded plan`
                    : "Get access to all portals and premium features"}
                </p>
                <div className="mt-4 bg-white/20 rounded-lg px-4 py-2 inline-block">
                  <span className="text-sm">Current Plan: </span>
                  <span className="font-semibold">{currentPlan}</span>
                </div>
              </div>
            </div>

            {/* Footer — Upgrade CTA takes the customer to /shop where
                they can pick a plan that includes the restricted portal. */}
            <div className="px-8 py-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-end bg-slate-50 rounded-b-2xl">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-100 transition-colors"
              >
                Maybe later
              </button>
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  navigate("/shop");
                }}
                className="px-6 py-2.5 rounded-lg bg-linear-to-r from-blue-600 to-purple-600 text-white font-semibold shadow-md hover:shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Upgrade
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpgradeModal;
